import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import CircularLoading from "../components/CircularLoading";
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import Button from '@mui/material/Button';
import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import SaveIcon from '@mui/icons-material/Save';

const useStyles = makeStyles(theme => ({
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: '#F9FAFB',
      '&:hover': {
        backgroundColor: '#F3F4F6',
      },
      '&.Mui-focused': {
        backgroundColor: '#FFFFFF',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E5E7EB',
    },
    '& .MuiInputLabel-root': {
      color: '#6B7280',
    },
  },
  rootRtl: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 20
    },
    "& legend": {
      textAlign: "right",
      marginRight: 20
    }
  },
}));

const AppInformation = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editSettings,
    clearSettingsViewError
  } = api;
  const settingsdata = useSelector(state => state.settingsdata);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [data, setData] = useState();
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (settingsdata.settings) {
      setData(settingsdata.settings);
    }
  }, [settingsdata.settings]);

  const handleTextChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (data.AllowCriticalEditsAdmin) {
      if (data.bonus === '') {
        alert(t('proper_bonus'))
      } else {
        setClicked(true);
        dispatch(editSettings(data));
      }
    } else {
      alert(t('demo_mode'));
    }
  }

  const handleClose = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };

  return (
    data ?
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('app_info')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('manage_app_settings') || 'Manage your application settings and company information'}
          </Typography>
        </Box>

        <Grid container spacing={3} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
          {/* Company Information Card */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#FFFFFF',
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  p: 2.5,
                  borderBottom: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: '#EEF2FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BusinessIcon sx={{ color: '#6366F1', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 16 }}>
                    {t('company_info') || 'Company Information'}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                    {t('basic_details') || 'Basic company details'}
                  </Typography>
                </Box>
              </Box>

              {/* Card Content */}
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="appName"
                  label={t('AppName')}
                  name="appName"
                  autoComplete="appName"
                  onChange={handleTextChange}
                  value={data.appName || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="CompanyName"
                  label={t('CompanyName')}
                  name="CompanyName"
                  autoComplete="CompanyName"
                  onChange={handleTextChange}
                  value={data.CompanyName || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="CompanyAddress"
                  label={t('CompanyAddress')}
                  name="CompanyAddress"
                  autoComplete="CompanyAddress"
                  onChange={handleTextChange}
                  value={data.CompanyAddress || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="CompanyWebsite"
                  label={t('CompanyWebsite')}
                  name="CompanyWebsite"
                  autoComplete="CompanyWebsite"
                  onChange={handleTextChange}
                  value={data.CompanyWebsite || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="contact_email"
                  label={t('contact_email')}
                  name="contact_email"
                  autoComplete="contact_email"
                  onChange={handleTextChange}
                  value={data.contact_email || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="CompanyPhone"
                  label={t('CompanyPhone')}
                  name="CompanyPhone"
                  autoComplete="CompanyPhone"
                  onChange={handleTextChange}
                  value={data.CompanyPhone || ''}
                />
              </Box>
            </Box>
          </Grid>

          {/* Links Card */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#FFFFFF',
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  p: 2.5,
                  borderBottom: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: '#DCFCE7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LinkIcon sx={{ color: '#16A34A', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: 16 }}>
                    {t('app_link') || 'App Links'}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                    {t('social_store_links') || 'Social media and store links'}
                  </Typography>
                </Box>
              </Box>

              {/* Card Content */}
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="CompanyTerms"
                  label={t('terms')}
                  name="CompanyTerms"
                  autoComplete="CompanyTerms"
                  onChange={handleTextChange}
                  value={data.CompanyTerms || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="FacebookHandle"
                  label={t('FacebookHandle')}
                  name="FacebookHandle"
                  autoComplete="FacebookHandle"
                  onChange={handleTextChange}
                  value={data.FacebookHandle || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="TwitterHandle"
                  label={t('TwitterHandle')}
                  name="TwitterHandle"
                  autoComplete="TwitterHandle"
                  onChange={handleTextChange}
                  value={data.TwitterHandle || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="InstagramHandle"
                  label={t('InstagramHandle')}
                  name="InstagramHandle"
                  autoComplete="InstagramHandle"
                  onChange={handleTextChange}
                  value={data.InstagramHandle || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="AppleStoreLink"
                  label={t('AppleStoreLink')}
                  name="AppleStoreLink"
                  autoComplete="AppleStoreLink"
                  onChange={handleTextChange}
                  value={data.AppleStoreLink || ''}
                />
                <TextField
                  className={`${classes.inputField} ${isRTL === "rtl" ? classes.rootRtl : ''}`}
                  variant="outlined"
                  fullWidth
                  size="small"
                  id="PlayStoreLink"
                  label={t('PlayStoreLink')}
                  name="PlayStoreLink"
                  autoComplete="PlayStoreLink"
                  onChange={handleTextChange}
                  value={data.PlayStoreLink || ''}
                />
              </Box>
            </Box>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{
                  bgcolor: '#111827',
                  color: '#FFFFFF',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 14,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#374151',
                    boxShadow: 'none',
                  },
                }}
              >
                {t('save_changes') || 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <AlertDialog open={settingsdata.error.flag && clicked} onClose={handleClose}>{t('update_failed')}</AlertDialog>
      </Box>
      :
      <CircularLoading />
  );
}

export default AppInformation;
